import clsx from "clsx";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import type { IIconName } from "@components/icons";
import { humanizedFileSize } from "@/utils";
import { IconButton } from "@/components/molecules/iconButton";
import { useFileViewer } from "@/hooks/useFileViewer";
import { FileViewerIconButton } from "@/components/tableColumns";
import { Icon } from "@components/atoms/icons";
import type {
  IFileCardIconButtonProps,
  IFileCardProps,
} from "@components/atoms/fileCard/types";
import { Typography } from "@components/atoms/typography";
import { Box } from "@components/atoms/box";

export function FileCard(props: IFileCardProps) {
  const {
    fileID,
    uploadedPercentage,
    status,
    canUpload = true,
    readOnly,
    file,
    onCancel,
    onDelete,
    onUpload,
  } = props;

  const { t } = useTranslation("common");

  const { fileData, canShow, isImage, isAudio, isVideo, isPdf, show } =
    useFileViewer(file);

  function getIconButtonProps(): IFileCardIconButtonProps {
    switch (status) {
      case "uploading":
        return {
          icon: <Icon name={"CloseCircle"} color={"danger-main"} />,
          onClick: () => {
            onCancel?.(fileID);
          },
          label: t("cancel"),
        };
      default:
        return {
          icon: <Icon name={"TrashBinTrash"} color={"danger-main"} />,
          onClick: () => {
            onDelete?.(fileID);
          },
          label: t("delete"),
        };
    }
  }

  const canShowProgress =
    uploadedPercentage !== undefined &&
    (status === "uploading" || status === "error" || status === "success");

  const getCardIcon = (): IIconName => {
    if (isImage) return "Gallery";
    if (isVideo) return "VideoFramePlayHorizontal";
    if (isPdf) return "Library";
    if (isAudio) return "MusicLibrary";
    return "File";
  };

  return (
    <Box px={"m3"} py={"m3"} radius={"md"} w100 className={classes.container}>
      <Box px={"m1"} py={"m1"} radius={"sm"} className={classes.icon}>
        <Icon name={getCardIcon()} />
      </Box>
      <div className={classes.content}>
        <div className={"d-flex justify-content-between"}>
          <Typography fontSize={"sm"} fontWeight={"semi-bold"}>{`${
            fileData?.name || "unknown"
          } (${humanizedFileSize(fileData?.size || 0)})`}</Typography>
          <div className={"flex-item-center gap-m1"}>
            {canShowProgress && (
              <Typography
                fontSize={"xs"}
                fontWeight={"semi-bold"}
              >{`${uploadedPercentage}%`}</Typography>
            )}
            {status === "success" && (
              <Icon
                name={"CheckCircle"}
                color={"success-dark"}
                fontSize={"base"}
                className={"me-sm"}
              />
            )}
            {status === "error" && (
              <Icon
                name={"DangerCircle"}
                color={"danger-dark"}
                fontSize={"base"}
                className={"me-sm"}
              />
            )}
          </div>
        </div>
        {canShowProgress && (
          <div className={clsx(["border ", classes.barWrapper])}>
            <div
              className={classes.bar}
              style={{ width: `${uploadedPercentage}%` }}
            />
          </div>
        )}
      </div>
      <div className={"flex-item-center gap-xs "}>
        <FileViewerIconButton
          canShow={canShow}
          onClick={show}
          url={fileData?.url || ""}
        />
        {!readOnly && (
          <>
            {(status === "idle" || status === "error") && canUpload && (
              <IconButton
                label={t("upload")}
                transparent
                className={classes.btn}
                icon={<Icon name={"CloudUpload"} color={"primary-main"} />}
                onClick={() => onUpload?.(fileID)}
              />
            )}
            <IconButton
              transparent
              className={classes.btn}
              {...getIconButtonProps()}
              label={t(getIconButtonProps().label)}
            />
          </>
        )}
      </div>
    </Box>
  );
}
